import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import {
  HdPlanetKey,
  getHdPlanetDisplayConfig,
} from '@/app/human-design/chart/bodygraph/planet-grid/hd-planet-display-config';
import { HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';

/**
 * Volgorde van de planeten op de cheat-sheet — gebaseerd op afstand tot
 * de aarde (Sun/Earth/Moon → nodes → inner-personal → outer-social → outer-trans),
 * gelijk aan {@link HdPlanetInfluenceCheatSheetPageComponent}. Chiron sluit aan
 * achteraan zodat alle 14 kaarten getoond worden.
 */
const HD_PLANET_CHEAT_SHEET_ORDER: readonly HdPlanetKey[] = [
  'sun', 'earth', 'moon',
  'northNode', 'southNode',
  'mercury', 'venus', 'mars',
  'jupiter', 'saturn',
  'uranus', 'neptune', 'pluto',
  'chiron',
];

interface HdPlanetCheatSheetLegendItem {
  readonly label: string;
  readonly color: string;
}

interface HdPlanetCheatSheetLegendGroup {
  readonly title?: string;
  readonly subtitle?: string;
  readonly items: readonly HdPlanetCheatSheetLegendItem[];
}

interface HdPlanetCheatSheetLegend {
  readonly title: string;
  readonly note: string;
  readonly columns: readonly (readonly HdPlanetCheatSheetLegendGroup[])[];
}

const COLOR_LIGHT = '#ffb800';
const COLOR_GROUNDING = '#1983bf';
const COLOR_INNER = '#d93c3c';
const COLOR_OUTER_SOCIAL = '#53D286';
const COLOR_OUTER_TRANS = '#773E9A';
/** Maan-kleur: zelfde amber als STRIPE_MOON (hd-planet-display-config) / het maan-icoon in de mandala. */
const COLOR_MOON = '#d4a028';

const HD_PLANET_CHEAT_SHEET_LEGEND_NL: HdPlanetCheatSheetLegend = {
  title: 'Legende',
  note: 'transit tijden zijn gemiddeldes per cyclus, per poort, per lijn',
  columns: [
    [
      {
        items: [
          { label: 'Zon', color: COLOR_LIGHT },
          { label: 'Aarde', color: COLOR_GROUNDING },
          { label: 'Maan', color: COLOR_MOON },
        ],
      },
      {
        title: 'Binnenste (pers.) planeten',
        subtitle: 'individuele processen, persoonlijke zaken',
        items: [
          { label: 'Mercurius', color: COLOR_INNER },
          { label: 'Venus', color: COLOR_INNER },
          { label: 'Mars', color: COLOR_INNER },
        ],
      },
    ],
    [
      {
        title: 'Posities',
        items: [
          { label: 'Noordknoop', color: COLOR_GROUNDING },
          { label: 'Zuidknoop', color: COLOR_GROUNDING },
        ],
      },
      {
        title: 'Sociale planeten',
        subtitle: 'socialiseren, zich organiseren, samenleving',
        items: [
          { label: 'Jupiter', color: COLOR_OUTER_SOCIAL },
          { label: 'Saturnus', color: COLOR_OUTER_SOCIAL },
        ],
      },
    ],
    [
      {
        title: 'Buitenste planeten',
        subtitle: 'transpersoonlijk, hoog bewustzijn vereist, traag, over generaties heen',
        items: [
          { label: 'Uranus', color: COLOR_OUTER_TRANS },
          { label: 'Neptunus', color: COLOR_OUTER_TRANS },
          { label: 'Pluto', color: COLOR_OUTER_TRANS },
        ],
      },
    ],
  ],
};

const HD_PLANET_CHEAT_SHEET_LEGEND_EN: HdPlanetCheatSheetLegend = {
  title: 'Legend',
  note: 'transit times are averages per cycle, per gate, per line',
  columns: [
    [
      {
        items: [
          { label: 'Sun', color: COLOR_LIGHT },
          { label: 'Earth', color: COLOR_GROUNDING },
          { label: 'Moon', color: COLOR_MOON },
        ],
      },
      {
        title: 'Inner (personal) planets',
        subtitle: 'individual processes, personal matters',
        items: [
          { label: 'Mercury', color: COLOR_INNER },
          { label: 'Venus', color: COLOR_INNER },
          { label: 'Mars', color: COLOR_INNER },
        ],
      },
    ],
    [
      {
        title: 'Positions',
        items: [
          { label: 'North Node', color: COLOR_GROUNDING },
          { label: 'South Node', color: COLOR_GROUNDING },
        ],
      },
      {
        title: 'Social planets',
        subtitle: 'socialising, organising, society',
        items: [
          { label: 'Jupiter', color: COLOR_OUTER_SOCIAL },
          { label: 'Saturn', color: COLOR_OUTER_SOCIAL },
        ],
      },
    ],
    [
      {
        title: 'Outer planets',
        subtitle: 'transpersonal, high consciousness required, slow, across generations',
        items: [
          { label: 'Uranus', color: COLOR_OUTER_TRANS },
          { label: 'Neptune', color: COLOR_OUTER_TRANS },
          { label: 'Pluto', color: COLOR_OUTER_TRANS },
        ],
      },
    ],
  ],
};

/**
 * Pagina die alle 14 planeten (incl. Chiron) als cheat-sheet-kaarten naast
 * elkaar toont. Eerste-fase implementatie van de planet cheat-sheet
 * (zie `hd-planets-info.md`).
 *
 * Toggles voor het verbergen van cyclus-info / bullets / subtitel — analoog
 * aan {@link HdCheatSheetPageComponent}. Wordt via DB-driven navigatie
 * onder een nieuw `Cheat sheets`-submenu gemount.
 */
@Component({
  selector: 'hd-planet-cheat-sheet-page',
  standalone: false,
  templateUrl: './hd-planet-cheat-sheet-page.component.html',
  styleUrl: './hd-planet-cheat-sheet-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdPlanetCheatSheetPageComponent {
  private readonly contentLang = inject(HdContentLanguageService);

  protected readonly hideCycleInfo = signal(false);
  protected readonly hideBullets = signal(false);
  protected readonly hideSubtitle = signal(false);

  protected readonly planets = HD_PLANET_CHEAT_SHEET_ORDER.map((key) =>
    getHdPlanetDisplayConfig(key),
  );

  /**
   * Legende-blok onderaan de cheat-sheet — vertaalde labels per taal-override.
   * Spiegelt het legende-blok rechtsonder uit de Figma cheat-sheet (`1042:1460`).
   */
  protected readonly legend = computed(() => {
    const lang = this.contentLang.effectiveLang();
    return lang === 'en' ? HD_PLANET_CHEAT_SHEET_LEGEND_EN : HD_PLANET_CHEAT_SHEET_LEGEND_NL;
  });

  protected toggleHideCycleInfo(): void { this.hideCycleInfo.set(!this.hideCycleInfo()); }
  protected toggleHideBullets(): void { this.hideBullets.set(!this.hideBullets()); }
  protected toggleHideSubtitle(): void { this.hideSubtitle.set(!this.hideSubtitle()); }
}
